import React from 'react';
import Icon from '../../../components/AppIcon';

const RevenueAnalyticsPanel = ({ statistics, invoices }) => {
  const calculateGrowthRate = () => {
    // Calculate month-over-month growth
    return '+12.5%';
  };

  const calculateLifetimeValue = () => {
    // Calculate average client lifetime value
    if (!statistics?.totalSpent || !statistics?.totalInvoices) return 0;
    return (statistics?.totalSpent / statistics?.totalInvoices) * 12; // Annualized
  };

  const getRevenueByTier = () => {
    // Aggregate revenue by subscription tier
    return [
      { tier: 'Basic', revenue: 15000, percentage: 25 },
      { tier: 'Standard', revenue: 30000, percentage: 50 },
      { tier: 'Premium', revenue: 12000, percentage: 20 },
      { tier: 'Enterprise', revenue: 3000, percentage: 5 }
    ];
  };

  const revenueByTier = getRevenueByTier();

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            <span className="text-sm font-medium text-success">{calculateGrowthRate()}</span>
          </div>
          <h3 className="text-sm font-medium text-foreground mb-1">Growth Rate</h3>
          <p className="text-2xl font-bold text-foreground">K{(statistics?.totalSpent || 0)?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">Monthly recurring revenue</p>
        </div>

        <div className="bg-success/5 rounded-lg p-4 border border-success/20">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Users" size={20} className="text-success" />
          </div>
          <h3 className="text-sm font-medium text-foreground mb-1">Client Lifetime Value</h3>
          <p className="text-2xl font-bold text-foreground">K{calculateLifetimeValue()?.toFixed(0)}</p>
          <p className="text-xs text-muted-foreground mt-1">Average annual value</p>
        </div>

        <div className="bg-warning/5 rounded-lg p-4 border border-warning/20">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={20} className="text-warning" />
          </div>
          <h3 className="text-sm font-medium text-foreground mb-1">Average Deal Size</h3>
          <p className="text-2xl font-bold text-foreground">K{(statistics?.averageMonthlySpend || 0)?.toFixed(0)}</p>
          <p className="text-xs text-muted-foreground mt-1">Per client per month</p>
        </div>
      </div>

      {/* Revenue by Tier */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Revenue Distribution by Tier</h3>
        <div className="space-y-4">
          {revenueByTier?.map((item) => (
            <div key={item?.tier}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{item?.tier}</span>
                <span className="text-sm font-semibold text-foreground">K{item?.revenue?.toLocaleString()}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${item?.percentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{item?.percentage}% of total revenue</p>
            </div>
          ))}
        </div>
      </div>

      {/* Growth Projections */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Growth Projections (PNG Kina)</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Next Quarter (Q2 2026)</span>
            <span className="text-sm font-semibold text-foreground">K{((statistics?.totalSpent || 0) * 1.125)?.toFixed(0)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Next Half Year (H2 2026)</span>
            <span className="text-sm font-semibold text-foreground">K{((statistics?.totalSpent || 0) * 1.25)?.toFixed(0)}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-muted-foreground">Annual Projection (2026)</span>
            <span className="text-sm font-semibold text-primary">K{((statistics?.totalSpent || 0) * 1.5)?.toFixed(0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalyticsPanel;